import type { CommonImageItemType } from '@/components-biz/ImageSelect';
import { parseContainerDeployConfigObject } from '@/delivery-type-plugin/utils/parseContainerDeployConfigObject';

export interface ContainerDeployInstanceSchedulingGpu {
  resourceType?: string;
  vendor?: string;
  device?: string;
  deviceType?: string;
  vendorID?: string;
  deviceID?: string;
  code?: string;
  driverVersion?: string;
  cudaVersion?: string;
}

export interface ContainerDeployInstanceScheduling {
  enabled?: boolean;
  minCpuCores?: number;
  minMemoryGi?: number;
  gpus?: ContainerDeployInstanceSchedulingGpu[];
}

export interface ContainerDeployEnvVarItem {
  key?: string;
  value?: string;
  description?: string;
  required?: boolean;
  editable?: boolean;
  visible?: boolean;
}

/** 节点资源约束是否生效（API 常仅配置 minCpuCores/minMemoryGi，无 enabled 字段） */
export function isContainerDeployInstanceSchedulingActive(scheduling?: ContainerDeployInstanceScheduling): boolean {
  if (!scheduling) return false;
  if (scheduling.enabled === false) return false;
  if (scheduling.enabled === true) return true;
  return (
    (Number(scheduling.minCpuCores) > 0 && Number.isFinite(Number(scheduling.minCpuCores))) ||
    (Number(scheduling.minMemoryGi) > 0 && Number.isFinite(Number(scheduling.minMemoryGi))) ||
    !!scheduling.gpus?.length
  );
}

function resolveMountPaths(
  config: Record<string, unknown> | null | undefined,
  singular: string,
  plural: string,
): {
  mountPath?: string;
  mountPaths?: string[];
} {
  if (!config || typeof config !== 'object') return {};
  const rawMount = config[singular];
  const pathsFromMountField = Array.isArray(rawMount)
    ? (rawMount as unknown[]).map((item) => String(item ?? '').trim()).filter(Boolean)
    : String(rawMount ?? '').trim()
      ? [String(rawMount).trim()]
      : [];
  const pathsFromPlural = Array.isArray(config[plural])
    ? (config[plural] as unknown[]).map((item) => String(item ?? '').trim()).filter(Boolean)
    : [];
  const paths = pathsFromPlural.length ? pathsFromPlural : pathsFromMountField;
  if (paths.length) return { mountPath: paths[0], mountPaths: paths };
  return {};
}

export function resolvePersistentVolumeMountBaseFromContainerDeployConfig(config?: Record<string, unknown> | null) {
  return resolveMountPaths(config, 'persistentVolumeMountPath', 'persistentVolumeMountPaths');
}

export function resolveTemporaryVolumeMountBaseFromContainerDeployConfig(config?: Record<string, unknown> | null) {
  return resolveMountPaths(config, 'temporaryVolumeMountPath', 'temporaryVolumeMountPaths');
}

/** L1 用：仅解析镜像挂载路径（不依赖 capabilities） */
export function resolveImageDeployVolumePathsFromItem(imageItem?: CommonImageItemType | null): {
  persistent: string[];
  ephemeral: string[];
} {
  if (!imageItem) return { persistent: [], ephemeral: [] };
  const config = parseContainerDeployConfigObject(imageItem.containerDeployConfig);
  const persistentInfo = resolvePersistentVolumeMountBaseFromContainerDeployConfig(config);
  const ephemeralInfo = resolveTemporaryVolumeMountBaseFromContainerDeployConfig(config);
  const persistent = persistentInfo.mountPaths?.length
    ? persistentInfo.mountPaths
    : persistentInfo.mountPath
      ? [persistentInfo.mountPath]
      : [];
  const ephemeral = ephemeralInfo.mountPaths?.length ? ephemeralInfo.mountPaths : ephemeralInfo.mountPath ? [ephemeralInfo.mountPath] : [];
  return { persistent, ephemeral };
}
